import axios from 'axios';
import { API_URL, API_KEY } from "../config/env";
import { toast } from 'react-toastify';
import { encrypt, decrypt } from '../utils/crypto';
import i18n from '../i18n/i18n';

//#region Language and Headers
const language = { tr: 1, en: 2, us: 2 };

const headers = {
  'Content-Type': 'application/json',
  accept: 'text/plain',
  ChannelId: '1',
};

// Dil başlıkları her istekte güncel dilden kurulur (dil değişince yenilemeden API içeriği de değişir)
const setLanguageHeaders = () => {
  const selectedLanguage = String(i18n.language || '').slice(0, 2).toLowerCase();
  const langCode = language[selectedLanguage];
  if (langCode) {
    headers['lang'] = langCode.toString();
    headers['language'] = selectedLanguage;
  }
  headers['x-root'] = `${API_KEY}`;
};

setLanguageHeaders();
//#endregion

//#region Action Handling
const showMessage = (message, type = 'error') => {
  // Eğer mesaj bir çeviri anahtarı ise, i18next'ten çeviriyi al
  const translatedMessage = message.startsWith('common.') || 
                           message.startsWith('errors.') ? 
                           i18n.t(message) : message;
  
  toast[type](translatedMessage, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "colored",
  });
};

const handleResponse = (response) => {
  const data = response.data;
  
  // HTTP durum kodu 200 değilse
  if (response.status !== 200) {
    if (response.status === 401) {
      handleUnauthorized();
      return null;
    }
    
    if (response.status === 403) {
      showMessage(i18n.t('errors.forbidden'), 'error');
      return null;
    }
    
    if (response.status === 404) {
      showMessage(i18n.t('errors.notFound'), 'error');
      return null;
    }
    
    // Backend'den gelen hata mesajını göster
    showMessage(data.message || i18n.t('errors.general'), 'error');
    
    // Hata detaylarını döndür
    return {
      statusCode: response.status,
      message: data.message || i18n.t('errors.general'),
      errors: data.errors || [],
      success: false
    };
  }
  
  // API kendi içinde hata döndürebilir (statusCode 200 dışında bir değer)
  if (data.statusCode && data.statusCode !== 200) {
    // Backend'den gelen hata mesajını göster
    showMessage(data.message || i18n.t('errors.general'), 'error');
    
    // Hata detaylarını döndür
    return {
      statusCode: data.statusCode,
      message: data.message || i18n.t('errors.general'),
      errors: data.errors || [],
      data: data.data,
      success: false
    };
  }

  // Başarılı response
  return {
    statusCode: response.status,
    data: data.data || data,
    message: data.message,
    token: data.token || data.data?.token,
    success: true
  };
};

const handleError = (error) => {
  if (axios.isAxiosError(error)) {
    const errorResponse = error.response?.data;
    // Backend'den gelen hata mesajını kullan, yoksa genel hata mesajı göster
    const errorMessage = errorResponse?.message || i18n.t('errors.general');
    
    showMessage(errorMessage, 'error');
    
    // Hata detaylarını konsola yazdır (geliştirme aşamasında faydalı)
    console.error('API Error:', errorResponse);
    
    return {
      statusCode: error.response?.status || 500,
      message: errorMessage,
      errors: errorResponse?.errors || [],
      success: false
    };
  }
  
  showMessage(i18n.t('errors.general'), 'error');
  return {
    statusCode: 500,
    message: i18n.t('errors.general'),
    success: false
  };
};
//#endregion

//#region HTTP Request Functions
const setAuthorizationHeader = () => {
  try {
    const encryptedToken = localStorage.getItem('authToken');
    if (encryptedToken) {
      const token = decrypt(encryptedToken);
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
  } catch (error) {
    // Token decrypt edilemezse veya geçersizse localStorage'dan temizle
    localStorage.removeItem('authToken');
  }
};

const request = async (method, url, data = null, isFileUpload = false) => {
  try {
    setLanguageHeaders();
    setAuthorizationHeader();

    if (isFileUpload) {
      headers['Content-Type'] = 'multipart/form-data';
    } else {
      headers['Content-Type'] = 'application/json';
    }

    const response = await axios({
      method,
      url,
      data: method === 'get' || method === 'delete' ? undefined : data,
      params: method === 'get' || method === 'delete' ? data : undefined,
      headers,
      validateStatus: (status) => true // Tüm status kodlarını kabul et
    });

    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

//#region Generic Request Functions
export const genericPost = async (url, data) => {
  try {
    return await request('post', url, data);
  } catch (error) {
    return handleError(error);
  }
};

export const genericGet = async (url, params) => {
  try {
    return await request('get', url, params);
  } catch (error) {
    return handleError(error);
  }
};

export const genericDelete = async (url, params) => {
  try {
    return await request('delete', url, params);
  } catch (error) {
    return handleError(error);
  }
};

export const genericPut = async (url, data) => {
  try {
    return await request('put', url, data);
  } catch (error) {
    return handleError(error);
  }
};

export const genericFilePost = async (url, formData) => {
  const response = await request('post', url, formData, true);
  
  // API'den gelen yanıtı kontrol et
  if (response && !response.success) {
    console.error('File upload error:', response);
  }
  
  return response;
};

export const genericFilePut = async (url, formData) => {
  const response = await request('put', url, formData, true);
  
  // API'den gelen yanıtı kontrol et
  if (response && !response.success) {
    console.error('File update error:', response);
  }
  
  return response;
};
//#endregion

// 401: sadece gerçekten oturum varken “session expired” say.
// x-root (API key) hatalarında token yoksa login'e atma / spam toast üretme.
const handleUnauthorized = () => {
  const hadToken = !!localStorage.getItem('authToken');
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');

  if (!hadToken) {
    showMessage(i18n.t('errors.forbidden'), 'error');
    return;
  }

  showMessage(i18n.t('errors.sessionExpired'), 'error');
  window.location.href = '/login';
}; 